import React, { useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContentText from "@mui/material/DialogContentText";
import { any, object, string, TypeOf } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, SubmitHandler } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import CircularProgress from "@mui/material/CircularProgress";
import { green } from "@mui/material/colors";
import { Grid, Box, Button, TextField, Divider } from "@mui/material";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";
import {
  generateToken,
  sendInvitation,
  clearInvitation,
} from "../../redux/actions/auth.actions";
import CopyToClipboard from "../../components/CopyToClipboard";
import TokenService from "../../utility";

interface Props {
  isDialogOpened: boolean;
  handleCloseDialog: any;
}

const registerSchema = object({
  email: string()
    .nonempty("Email is required")
    .regex(/^(\s?[^\s,]+@[^\s,]+\.[^\s,]+\s?,)*(\s?[^\s,]+@[^\s,]+\.[^\s,]+)$/),
  token: string(),
});

const invitationSchema = object({
  company_id: string(),
  name: any(),
});

type RegisterInput = TypeOf<typeof registerSchema>;
type InvitationInput = TypeOf<typeof invitationSchema>;

export const InvitationForm = (props: Props) => {
  const dispatch = useDispatch();
  const [link, setLink] = React.useState(false);
  const {
    register,
    reset,
    formState: { errors, isValid },
    handleSubmit,
  } = useForm<RegisterInput>({
    resolver: zodResolver(registerSchema),
    mode: "onChange",
  });
  const {
    register: register1,
    reset: reset1,
    handleSubmit: handleSubmit1,
  } = useForm<InvitationInput>({ resolver: zodResolver(invitationSchema) });
  const { token } = useSelector((state: any) => state.auth);
  const ui = useSelector((state: any) => state.UI);
  const user = TokenService.getTokenUser();

  const handleClose = () => {
    props.handleCloseDialog(false);
    reset({ email: "", token: "" });
    reset1({ name: "" });
    setLink(false);
  };

  const onSubmitEmail: SubmitHandler<InvitationInput> = (data) => {
    setLink(true);
    dispatch<any>(generateToken(data));
  };

  const onSubmit: SubmitHandler<RegisterInput> = (data) => {
    dispatch<any>(sendInvitation(data));
  };

  useEffect(() => {
    if (token) {
      reset1({ name: process.env.REACT_APP_URL + "signup/" + token });
      reset({ token: process.env.REACT_APP_URL + "signup/" + token });
    }
  }, [token, reset, reset1]);

  return (
    <Dialog open={props.isDialogOpened} onClose={handleClose}>
      <DialogTitle>Invitation</DialogTitle>
      <DialogContent>
        <Box
          component="form"
          noValidate
          onSubmit={handleSubmit1(onSubmitEmail)}
        >
          <TextField
            id="company_id"
            type="hidden"
            sx={{ display: "none" }}
            value={user.company}
            {...register1("company_id")}
          />
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <DialogContentText>
                Invite employees with sending a unique link to recipient email
                address.
              </DialogContentText>
              {!link && (
                <Box sx={{ m: 1, position: "relative" }}>
                  <Button
                    size="small"
                    variant="contained"
                    sx={{ mt: 2, mb: 2 }}
                    type="submit"
                    disabled={ui.loading}
                  >
                    Generate Link
                  </Button>
                  {ui.loading && (
                    <CircularProgress
                      size={24}
                      sx={{
                        color: green[500],
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        marginTop: "-12px",
                        marginLeft: "-12px",
                      }}
                    />
                  )}
                </Box>
              )}
              {link && (
                <>
                  <TextField
                    autoFocus
                    margin="dense"
                    id="name"
                    sx={{ width: "100%" }}
                    variant="standard"
                    InputProps={{ style: { fontSize: 10 } }}
                    {...register1("name")}
                  />
                  <CopyToClipboard>
                    {({ copy }) => (
                      <Button
                        variant="contained"
                        color="primary"
                        sx={{ mb: 2, mr: 1 }}
                        onClick={() =>
                          copy(process.env.REACT_APP_URL + "signup/" + token)
                        }
                        startIcon={<ContentPasteIcon />}
                      >
                        Copy
                      </Button>
                    )}
                  </CopyToClipboard>
                  <Button
                    variant="outlined"
                    color="error"
                    sx={{ mb: 2 }}
                    onClick={() => {
                      setLink(false);
                      dispatch<any>(clearInvitation());
                      reset({ email: "", token: "" });
                    }}
                  >
                    Delete
                  </Button>
                </>
              )}
            </Grid>
          </Grid>
        </Box>
        <Divider sx={{ mb: 2 }} />
        {token && link && (
          <Box
            component="form"
            id="myform"
            noValidate
            onSubmit={handleSubmit(onSubmit)}
          >
            <DialogContentText>
              To send invitation by email, please enter recipients email address
              here seperating by comma.
            </DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              id="name"
              label="Email Address"
              fullWidth
              variant="standard"
              required
              error={!!errors["email"]}
              {...register("email")}
              helperText={errors["email"] ? errors["email"].message : ""}
            />
            <TextField
              id="token"
              type="hidden"
              sx={{ display: "none" }}
              {...register("token")}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Box sx={{ m: 1, position: "relative" }}>
          <Button
            type="submit"
            form="myform"
            variant="contained"
            color="success"
            disabled={!isValid || ui.loading}
          >
            Send
          </Button>
          {ui.loading && (
            <CircularProgress
              size={24}
              sx={{
                color: green[500],
                position: "absolute",
                top: "50%",
                left: "50%",
                marginTop: "-12px",
                marginLeft: "-12px",
              }}
            />
          )}
        </Box>

        <Button
          onClick={handleClose}
          variant="contained"
          color="error"
          disabled={ui.loading}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};
